import { TICK_RATE } from "./constants.ts";

/**
 * Interactions with the map (docs/DECISIONS.md, D38): the server runs them,
 * the replicated state tells the clients what to draw and, for the hatches,
 * which floor the prediction stands on.
 */

/** Phase of a trap (lever + hatches), replicated as `ArenaState.trap`. */
export const TrapPhase = {
  /** The lever can be pulled. */
  Ready: 0,
  /** Pulled: the hatches flash, there is time to get off them. */
  Warning: 1,
  /** Open: the hatch solids are gone. */
  Open: 2,
  /** Closed again, the lever reloads. */
  Recharging: 3,
} as const;
export type TrapPhase = (typeof TrapPhase)[keyof typeof TrapPhase];

export const TRAP_TICKS = {
  [TrapPhase.Warning]: Math.round(1.5 * TICK_RATE),
  [TrapPhase.Open]: Math.round(2.5 * TICK_RATE),
  [TrapPhase.Recharging]: 12 * TICK_RATE,
} as const;

/** What the crowd throws into the arena. */
export const PickupKind = {
  /** The apple: Super Slam meter filled by one Super's worth. */
  Apple: 1,
  /** The amphora of wine: stronger knockback for a while. */
  Amphora: 2,
} as const;
export type PickupKind = (typeof PickupKind)[keyof typeof PickupKind];

/** A fighter's temporary bonus, replicated as `PlayerState.buff`. */
export const Buff = {
  None: 0,
  /** From the amphora: every hit pushes harder. */
  Strength: 1,
} as const;
export type Buff = (typeof Buff)[keyof typeof Buff];

export const CROWD = {
  /** Applause meter, 0–max: when full, the crowd throws objects and it starts again. */
  max: 100,
  /** Gained every second by itself, so even a quiet arena gets its objects. */
  perSecond: 2,
  perHit: 3,
  perSlamTarget: 12,
  perRingOut: 15,
  /** Flight from the stands to the floor: the shadow on the floor warns where it lands. */
  fallTicks: Math.round(1.1 * TICK_RATE),
  /** A thrown object left on the floor disappears after this long. */
  lifetimeTicks: 15 * TICK_RATE,
  /** Distance from a fighter's centre at which it is picked up. */
  pickupRadius: 0.9,
  strengthTicks: 8 * TICK_RATE,
  strengthKnockback: 1.35,
} as const;

/** Where an object of the map is, replicated as `PropState.status`. */
export const PropStatus = {
  /** In its place (or where it was dropped). */
  Here: 0,
  /** In someone's hands (`PropState.carrier`). */
  Carried: 1,
  /** Broken, or flying after a throw: gone until it respawns. */
  Gone: 2,
} as const;
export type PropStatus = (typeof PropStatus)[keyof typeof PropStatus];

export const PROPS = {
  /** How far a fighter reaches to pick up an object (B with free hands). */
  grabReach: 1.4,
  /** Damage of a melee hit on an object; launchers and abilities count double. */
  hitDamage: 1,
  strongDamage: 2,
  /** A thrown object breaks what it hits. */
  throwDamage: 3,
} as const;
